import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';

import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import { useAufsichtStore } from '../../store/useAufsichtStore';
import { Dialog } from '../../ui/Dialog';
import styles from './AufsichtMitteilung.module.css';
import { bestaetigtDurch } from './mitteilungGeste';
import { mitteilungZeigen } from './mitteilungZeigen';

/**
 * Eine Mitteilung der Prüfungsleitung (Relay `MESSAGE`). Sie richtet sich an den Prüfer
 * (Entscheidung des Nutzers, 2026-10-07) und muss bestätigt werden: draufgetippt oder in
 * jede Richtung weggewischt (Entscheidung des Nutzers, 2026-10-08, `mitteilungGeste.ts`). Ein
 * Tipp daneben tut nichts. Erst danach quittiert der Abgleich sie bei Relay. Mehrere kommen
 * nacheinander, älteste zuerst.
 *
 * ⚠️ **Der Knopf hat keinen eigenen Klick für Finger und Maus.** Die Fläche bestätigt schon beim
 * Loslassen; ein zweiter Klick danach träfe dieselbe Stelle, an der jetzt die nächste
 * Mitteilung steht, und bestätigte sie ungelesen. Nur die Tastatur (`detail === 0`) geht über
 * den Knopf.
 *
 * ⚠️ **Reiner Text.** Sie kommt von aussen; als HTML gezeigt, könnte sie die Oberfläche
 * verändern. `white-space: pre-wrap` erhält ihre Zeilen.
 *
 * Während eines Bogenabzugs und über der Scanbühne wartet sie (`mitteilungZeigen.ts`).
 */
export function AufsichtMitteilung() {
  const t = useT();
  const erste = useAufsichtStore((s) => s.offen[0]);
  const bestaetige = useAufsichtStore((s) => s.bestaetige);
  const zeigen = useAppStore((s) => mitteilungZeigen({ shotStamp: s.shotStamp, sheetKind: s.sheet.kind }));
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  const [versatz, setVersatz] = useState({ x: 0, y: 0 });
  const textRef = useRef<HTMLParagraphElement>(null);
  // Ein langer Text muss sich noch rollen lassen — dann gehört das senkrechte Ziehen über ihm
  // dem Rollen, sonst überall dem Wischen.
  const [rollbar, setRollbar] = useState(false);
  const text = erste?.text;
  useLayoutEffect(() => {
    const el = textRef.current;
    setRollbar(!!el && el.scrollHeight > el.clientHeight);
  }, [text, zeigen]);

  if (!erste || !zeigen) return null;

  const zurueck = () => {
    start.current = null;
    setVersatz({ x: 0, y: 0 });
  };
  const runter = (e: PointerEvent<HTMLDivElement>) => {
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const ziehen = (e: PointerEvent<HTMLDivElement>) => {
    const s0 = start.current;
    if (!s0 || s0.id !== e.pointerId) return;
    setVersatz({ x: e.clientX - s0.x, y: e.clientY - s0.y });
  };
  const hoch = (e: PointerEvent<HTMLDivElement>) => {
    const s0 = start.current;
    zurueck();
    if (!s0 || s0.id !== e.pointerId) return;
    if (bestaetigtDurch(e.clientX - s0.x, e.clientY - s0.y) !== null) bestaetige(erste.id);
  };
  return (
    <Dialog
      kicker={t.aufsicht.kicker}
      title={t.aufsicht.titel}
      onClose={() => undefined}
      dismissible={false}
      width={480}
    >
      <div
        className={styles.flaeche}
        style={{ transform: `translate(${versatz.x}px, ${versatz.y}px)` }}
        onPointerDown={runter}
        onPointerMove={ziehen}
        onPointerUp={hoch}
        onPointerCancel={zurueck}
      >
        <p ref={textRef} className={styles.text} data-rollbar={rollbar || undefined}>
          {erste.text}
        </p>
        <button
          type="button"
          className={`tap ${styles.gelesen}`}
          onClick={(e) => {
            if (e.detail === 0) bestaetige(erste.id);
          }}
        >
          {t.aufsicht.gelesen}
        </button>
      </div>
    </Dialog>
  );
}
